import { GA4_MEASUREMENT_ID } from '../config/env';
import { PRODUCTION_HOST, isProductionHost } from '../config/productionHost';
import { hasAnalyticsConsent } from './cookieConsent';

/**
 * GA4 for the app's pre-authentication pages ONLY.
 *
 * Why this exists: bowerlabs.ai measures visitors in GA4 and app.bowerlabs.ai
 * did not measure anything, so "visited the pricing page, then signed up" was
 * two unrelated halves and signup attribution was guesswork. Sharing the
 * marketing property closes that funnel.
 *
 * Why it is this narrow: the app sits inside Bower's HIPAA boundary, and
 * PostHog already covers product analytics behind its own PHI gate. A Google
 * tag that ran on authenticated routes would be the 2026-08-09 session-replay
 * gap again — a client SDK shipping page content straight to a vendor that is
 * not BAA-covered, where the server-side `mayEgressContent` boundary never
 * gets a say. See `.claude/rules/egress-policy.md`.
 *
 * Four independent conditions must ALL hold before a single hit is sent, and
 * the module fails closed on each:
 *
 *   1. The path is an exact match in PRE_AUTH_TRACKED_PATHS below.
 *   2. No session has authenticated during this page's lifetime (sealed).
 *   3. The visitor granted analytics consent.
 *   4. The host is production.
 *
 * And no hit ever carries a query string, a fragment, or document.title.
 * That is not caution for its own sake — see the exclusions below.
 */

/**
 * Credential-entry paths with nothing sensitive in the URL and no UGC on the
 * page. Exact match only, so `/auth/action` can never satisfy `/auth`.
 *
 * Deliberately EXCLUDED, each for a concrete reason — do not add these back:
 *   /auth/action, /verify-email, /recover-email  Firebase oobCode in the query
 *   /m/capture                                   magic-link token in the query
 *   /connector/callback (ROUTES.connectorCallback) OAuth authorization code
 *   /auth/workos                                 SSO handoff, carries state
 *   ROUTES.share.pattern                         public share pages render
 *                                                user content, which can be PHI
 *
 * `/auth` IS tracked, but note it also serves `?invitation=<token>`. That is
 * safe only because this module never transmits a query string. If that ever
 * changes, `/auth` has to come out of this set.
 */
const PRE_AUTH_TRACKED_PATHS: ReadonlySet<string> = new Set([
  '/auth',
  '/login',
  '/reset-password',
  '/verify-email-prompt',
]);

/** A constant, never document.title, which can name a workspace or artifact. */
const SAFE_PAGE_TITLE = 'Bower — sign in';

/**
 * The marketing site's GA4 stream. The app must NEVER report into it.
 *
 * Enhanced Measurement is ON for that stream and is a per-stream setting, so it
 * cannot be disabled for app.bowerlabs.ai while leaving it on for
 * bowerlabs.ai. Enhanced Measurement attaches its own listeners to the
 * document for the document's whole lifetime, and the app is a single-page app:
 * the document that loads gtag on `/auth` is still the document the user is in
 * on `/notes/<id>` after signing in. Every gate in this file guards OUR call
 * into gtag; none of them can stop gtag's own listeners. Downloading an
 * attachment would then emit `file_download` carrying the signed GCS URL and
 * the file name, which in a research app is exactly the content this module
 * exists to keep out of GA4.
 *
 * RESOLVED 2026-08-22: the app has its own stream, "Bower App (pre-auth only)"
 * (15480206550, G-LJZ76SGW8C), with Enhanced Measurement OFF and
 * bowerlabs.ai in its cross-domain list so the client id still carries over
 * and the funnel stays joined. So this guard no longer blocks anything; it
 * stays as a regression guard, because repointing the app at the marketing
 * stream would silently reintroduce the whole problem.
 *
 * Deliberately a comparison against the known-unsafe ID rather than a boolean
 * anyone can flip: the only way to satisfy it is to actually point the app at a
 * different stream. If a THIRD stream is ever added, the safe-by-construction
 * property depends on Enhanced Measurement being off there too, and that is not
 * something this file can check.
 */
const MARKETING_STREAM_MEASUREMENT_ID = 'G-Y3X287QJ3X';

/**
 * Reduces a referrer to its origin, and drops it entirely when it is our own.
 *
 * gtag populates `page_referrer` from `document.referrer` automatically, and
 * `api.service.ts` reaches `/auth` by assigning `window.location.href` — a full
 * document navigation from whatever path the session expired on. So the
 * referrer arrives as a real in-app deep URL: `/notes/<id>`, or worse
 * `/share/<token>`, whose token is a bearer credential and whose page can
 * render PHI. `/share/*` is kept off the allowlist for exactly that reason, and
 * the referrer would have walked it straight back in.
 *
 * Blanking every referrer would also work but would kill the funnel this module
 * exists for, since a marketing-site referrer is the signal. Origin-only keeps
 * that and structurally cannot carry a path.
 *
 * Our `Referrer-Policy` (firebase.json) does not help here: it governs what
 * THIS site sends, not the referrer another site hands us in
 * `document.referrer`.
 */
function safeReferrer(): string {
  try {
    const raw = document.referrer;
    if (!raw) return '';
    const origin = new URL(raw).origin;
    return origin === `https://${PRODUCTION_HOST}` ? '' : origin;
  } catch {
    return '';
  }
}

type GtagFn = (...args: unknown[]) => void;
type GaWindow = Window & { dataLayer?: unknown[]; gtag?: GtagFn };

/**
 * Once true, never false again for this page's lifetime.
 *
 * Signing out clears the user id but leaves authenticated DOM painted and the
 * router able to land back on `/auth`. A gate keyed on "is there a user right
 * now" would re-open capture at exactly that moment, so the seal is sticky
 * until a full page load — the same reasoning as `isContentCaptureAllowed` in
 * analytics.ts being sticky for the page.
 */
let sealed = false;
let initialised = false;
let lastTrackedPath: string | null = null;

/** Call as soon as a session is known to be authenticated. Irreversible. */
export function sealGa4ForAuthenticatedSession(): void {
  sealed = true;
}

/** @internal Test seam only. Never call from app code. */
export function __resetGa4StateForTests(): void {
  sealed = false;
  initialised = false;
  lastTrackedPath = null;
}

function isEligible(path: string): boolean {
  if (sealed) return false;
  if (!GA4_MEASUREMENT_ID) return false;
  // See MARKETING_STREAM_MEASUREMENT_ID: sharing that stream means inheriting
  // its Enhanced Measurement, which keeps collecting after the seal.
  if (GA4_MEASUREMENT_ID === MARKETING_STREAM_MEASUREMENT_ID) return false;
  if (typeof window === 'undefined') return false;
  // Shared with the PostHog gate in analytics.ts — one host allowlist, one
  // discriminator, one fail-closed default. See config/productionHost.ts.
  if (!isProductionHost()) return false;
  if (!hasAnalyticsConsent()) return false;
  return PRE_AUTH_TRACKED_PATHS.has(path);
}

function initialise(win: GaWindow, path: string): void {
  win.dataLayer = win.dataLayer || [];
  const gtag: GtagFn = function gtag() {
    // gtag requires the raw `arguments` object, not an array copy.
    // eslint-disable-next-line prefer-rest-params
    (win.dataLayer as unknown[]).push(arguments);
  };
  win.gtag = gtag;

  // Bower runs no advertising, so every ad signal stays denied outright.
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'granted',
    functionality_storage: 'granted',
    security_storage: 'granted',
  });

  gtag('js', new Date());
  gtag('config', GA4_MEASUREMENT_ID, {
    // The single most important line here. gtag's automatic page_view reads
    // location.href, which on /auth includes ?invitation=<token>. Turning it
    // off and sending sanitised hits by hand is what keeps tokens out of GA4.
    send_page_view: false,
    page_location: `https://${PRODUCTION_HOST}${path}`,
    page_title: SAFE_PAGE_TITLE,
    // Set explicitly, or gtag reads document.referrer itself. See safeReferrer.
    page_referrer: safeReferrer(),
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  initialised = true;
}

/** `_ga` and `_ga_<container>`; `_gat`, `_gid` and lookalikes are not ours. */
const GA4_COOKIE_NAME = /^_ga(_|$)/;

/**
 * Tears GA4 down after analytics consent is withdrawn (GDPR Art. 7(3)).
 *
 * isEligible already stops new hits once consent is gone, but that left gtag
 * with analytics_storage granted for the rest of the document and the `_ga`
 * cookies in the browser for their two-year lifetime.
 *
 * Cookie cleanup does NOT depend on gtag having loaded in this document: the
 * usual revocation is from Settings, an authenticated page where GA4 is sealed,
 * while the cookies date from an earlier visit to /auth. gtag's cookie_domain
 * 'auto' writes them to the parent domain (.bowerlabs.ai, shared with the
 * marketing site), so each one is expired host-only, on the host, and on the
 * parent. With no `_ga` cookie and no gtag, this does nothing.
 *
 * Off the production host it leaves cookies alone: GA4 never loads there, so a
 * `_ga` cookie visible on staging or localhost is production's or the marketing
 * site's, inherited from .bowerlabs.ai, and declining on staging is not a
 * decision about either.
 */
export function revokeGa4Consent(): void {
  if (typeof window === 'undefined') return;
  try {
    (window as GaWindow).gtag?.('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    if (!isProductionHost()) return;

    // Past the gate the host is app.bowerlabs.ai, so its parent is .bowerlabs.ai.
    const host = window.location.hostname;
    const domains = ['', `; domain=${host}`, `; domain=${host.slice(host.indexOf('.'))}`];

    const ga4CookieNames = () =>
      document.cookie
        .split(';')
        .map((pair) => pair.split('=')[0].trim())
        .filter((name) => GA4_COOKIE_NAME.test(name));

    for (const name of ga4CookieNames()) {
      for (const domain of domains) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
      }
    }
    // Fail loud: a cookie scoped to a domain or path not covered above survives.
    const survivors = ga4CookieNames();
    if (survivors.length > 0) console.warn('GA4 cookies survived revocation:', survivors);
  } catch (error) {
    console.warn('Failed to revoke GA4 consent:', error);
  }
}

/**
 * Sends one sanitised page_view for an allowlisted pre-auth path.
 *
 * Safe to call on every navigation: ineligible paths are dropped, and a repeat
 * of the same path is ignored so a re-render cannot inflate the count.
 */
export function trackPreAuthPageView(path: string): void {
  if (!isEligible(path)) return;
  if (path === lastTrackedPath) return;
  lastTrackedPath = path;

  const win = window as GaWindow;
  if (!initialised) initialise(win, path);

  // page_location is rebuilt from the allowlisted path, never read from the
  // address bar, so a query string cannot leak in even on a later navigation.
  win.gtag?.('event', 'page_view', {
    page_location: `https://${PRODUCTION_HOST}${path}`,
    page_title: SAFE_PAGE_TITLE,
    page_referrer: safeReferrer(),
  });
}
